import $ from 'jquery';

import { getAmplitudeEventPropertiesForProduct } from 'chairisher/analytics/product';
import {
    isMiniCart,
    isPreviousInvalidOffer,
    isPrivateOffer,
    isSellerInitiatedOffer,
} from 'chairisher/context/purchase';
import { getRegisterInteractionUrl } from 'chairisher/context/tracking';
import { getUrlName } from 'chairisher/context/site';
import { AmplitudeEventProperty, logAmplitudeEvent } from 'chairisher/util/analytics';
import { postJSON } from 'chairisher/util/fetch';
import { handleAsyncError } from 'chairisher/util/logging';
import { getProcessingFeeValue } from 'chairisher/view/checkout/receipt';

const paymentMethodValueToStringDict = {
    B: 'paypal',
    S: 'credit card',
    apple_pay: 'apple pay',
    google_pay: 'google pay',
};

/**
 * @param {string} paymentMethodValue
 * @param {boolean} isExpressCheckout
 * @returns {string}
 */
function getPaymentMethodName(paymentMethodValue, isExpressCheckout) {
    const paymentMethodName = paymentMethodValueToStringDict[paymentMethodValue] || paymentMethodValue;
    return isExpressCheckout ? `${paymentMethodName} express` : paymentMethodName;
}

/**
 * Creates an event name based on the checkout screen being used (cart, minicart, MAO)
 *
 * @param {string} name The name of the un-prefixed event
 * @returns {string} The event name with the appropriate prefix
 */
function generateEventName(name) {
    let prefix;

    switch (getUrlName()) {
        case 'cart_list':
            prefix = 'cart';
            break;
        case 'offer_counter':
            prefix = 'offer counter';
            break;
        case 'offer_create':
            prefix = 'offer';
            break;
        case 'offer_reactivate':
            prefix = 'offer reactivate';
            break;
        default:
            if (isMiniCart()) {
                prefix = 'minicart';
            }
    }

    return `${prefix} - ${name}`;
}

/**
 * Generates a sorted, unique array of lower case field names that have errors
 *
 * @param {Object} data
 * @returns {Array} Sorted, unique array of lower case field names that have errors
 */
function getFieldNamesWithErrors(data) {
    const fieldNames = [];

    const getErrorsFromObj = (obj) => {
        $.each(obj, (k, v) => {
            if (v && v.errors && v.errors.length) {
                if ($.inArray(k, fieldNames) === -1) {
                    fieldNames.push(k.toLowerCase());
                }
            }
        });
    };

    $.each(data, (key, value) => {
        if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i += 1) {
                getErrorsFromObj(value[i]);
            }
        } else {
            getErrorsFromObj(value);
        }
    });

    fieldNames.sort();
    return fieldNames;
}

/**
 * @param {ProductModel} productModel
 * @param {string} position
 */
export async function trackMaoModalOpen(productModel, position) {
    logAmplitudeEvent('make an offer modal - open', {
        ...getAmplitudeEventPropertiesForProduct(productModel),
        [AmplitudeEventProperty.POSITION]: position,
    });

    try {
        await postJSON(getRegisterInteractionUrl(), { interaction_type: 'I' }); // 'I' == Offer Initiate
    } catch (error) {
        handleAsyncError(error);
    }
}

/**
 * During finalization, we ensure that the rendered total matches the current calculated total on the server.
 * This is a safety guard to ensure the charges matches what the user agreed to.
 * A mismatch, while rare, can happen for variety of reasons, since the receipt is composed of many dynamic elements.
 * The purpose of this function is to check if there was a mismatch, and if so, report the line items that differed.
 *
 * @param {Object} previousPurchaseData The last purchase data that was stored
 * @param {Object} newPurchaseData The purchase data that was just returned during finalization
 * @param {string} paymentMethod The payment method string being used
 */
export function reportTotalMismatchDuringFinalization(previousPurchaseData, newPurchaseData, paymentMethod) {
    const getReceiptItems = (purchaseData) => purchaseData?.purchase_details?.receipt_summary?.value || [];
    const getTotalValue = (receiptItems) => receiptItems.find((item) => item.type === 'total')?.value;
    const diffItem = (previousItem, newItem) => (parseFloat(newItem) - parseFloat(previousItem)).toFixed(2);

    const previousReceipt = getReceiptItems(previousPurchaseData);
    const newReceipt = getReceiptItems(newPurchaseData);
    const previousTotal = getTotalValue(previousReceipt);
    const newTotal = getTotalValue(newReceipt);

    if (
        // skip totals that are $0.00 since this indicates an error somewhere else in the purchase response.
        parseFloat(previousTotal) &&
        parseFloat(newTotal) &&
        previousTotal !== newTotal &&
        previousReceipt.length === newReceipt.length
    ) {
        const totalDiff = diffItem(previousTotal, newTotal);
        const amplitudeProperties = {
            'total diff': totalDiff,
            [AmplitudeEventProperty.PAYMENT_METHOD]: paymentMethod,
        };

        previousReceipt.forEach(({ label: previousLabel, value: previousValue }, i) => {
            const { label: newLabel, value: newValue } = newReceipt[i];

            if (newLabel === previousLabel) {
                const propertyName = newLabel.toLowerCase();
                const itemDiff = diffItem(previousValue, newValue);
                if (itemDiff !== '0.00') {
                    amplitudeProperties[`${propertyName} diff`] = itemDiff;
                    amplitudeProperties[`${propertyName} new`] = newValue;
                    amplitudeProperties[`${propertyName} previous`] = previousValue;
                }
            }
        });

        logAmplitudeEvent('total mismatch during finalization', amplitudeProperties);
    }
}

/**
 * Tracks when a shipping address is submitted but comes back with errors
 */
export function trackShippingAddressSubmitError() {
    logAmplitudeEvent(generateEventName('shipping address submit error'));
}

/**
 * Tracks when a shipping address is submitted and comes back without errors
 */
export function trackShippingAddressSubmitSuccess() {
    logAmplitudeEvent(generateEventName('shipping address submit success'));
}

/**
 * Tracks when the shipping method section is submitted with errors
 */
export function trackShippingMethodSubmitError() {
    logAmplitudeEvent(generateEventName('shipping method submit error'));
}

/**
 * Tracks when the shipping method section is submitted without errors
 */
export function trackShippingMethodSubmitSuccess() {
    logAmplitudeEvent(generateEventName('shipping method submit success'));
}

/**
 * Tracks when a user tries to redeem a discount code or gift card but an error comes back
 *
 * @param {string} discountCode The discount code that was entered
 */
export function trackDiscountSubmitError(discountCode) {
    logAmplitudeEvent('cart - discount submit error', { [AmplitudeEventProperty.CODE]: discountCode });
}

/**
 * Tracks when a user successfully redeems a discount code
 *
 * @param {string} discountCode The discount code that was entered
 */
export function trackDiscountSubmitSuccess(discountCode) {
    logAmplitudeEvent('cart - discount submit success', {
        [AmplitudeEventProperty.CODE]: discountCode,
    });
}

/**
 * Tracks when a user successfully redeems a gift card
 *
 * @param {string} code The gift card code that was applied
 */
export function trackGiftCardApplied(code) {
    logAmplitudeEvent('cart - gift card applied', { [AmplitudeEventProperty.CODE]: code });
}

/**
 * Track when a user selects a payment method
 *
 * @param {string} paymentMethodValue
 * @param {boolean} isExpressCheckout
 * @param {boolean} shouldTrackProcessingFee
 */
export function trackPaymentMethodSelection(
    paymentMethodValue,
    isExpressCheckout = true,
    shouldTrackProcessingFee = false,
) {
    const eventObject = {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
    };
    if (shouldTrackProcessingFee) {
        eventObject[AmplitudeEventProperty.PROCESSING_FEE_AMOUNT] = getProcessingFeeValue();
    }
    logAmplitudeEvent(generateEventName('payment method select'), eventObject);
}

/**
 * Tracks when a user submits the payment section with an error.
 */
export function trackPaymentSubmitError() {
    logAmplitudeEvent(generateEventName('payment submit error'));
}

/**
 * Tracks when a user submits the payment section without an error.
 *
 * @param {string} paymentMethodValue
 * @param {boolean} isExpressCheckout
 */
export function trackPaymentSubmitSuccess(paymentMethodValue, isExpressCheckout = true) {
    logAmplitudeEvent(generateEventName('payment submit success'), {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
        [AmplitudeEventProperty.PROCESSING_FEE_AMOUNT]: getProcessingFeeValue(),
    });
}

/**
 * This should be called after the server has completed the payment.
 *
 * @param {string} paymentMethodValue
 * @param {number} totalCartValue
 * @param {string=} expressCheckoutOrigin The origin of the express checkout, if any
 */
export function trackPaymentComplete(paymentMethodValue, totalCartValue, expressCheckoutOrigin) {
    const eventObject = {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, !!expressCheckoutOrigin),
        [AmplitudeEventProperty.PRICE]: totalCartValue,
    };
    if (expressCheckoutOrigin) {
        eventObject[AmplitudeEventProperty.EXPRESS_CHECKOUT_ORIGIN] = expressCheckoutOrigin;
    }
    if (isPrivateOffer()) {
        eventObject[AmplitudeEventProperty.IS_PRIVATE_OFFER] = true;
        eventObject[AmplitudeEventProperty.IS_SELLER_INITIATED_OFFER] = isSellerInitiatedOffer();
    }
    if (isPreviousInvalidOffer()) {
        eventObject[AmplitudeEventProperty.IS_PREVIOUS_INVALID_OFFER] = true;
    }
    logAmplitudeEvent(generateEventName('payment complete'), eventObject);
}

/**
 * Tracks when a shipping method is selected on cart or offer in amplitude
 * @param {number} productId The id of the product that we are tracking
 * @param {string} productTaxonomy The taxonomy string of the product
 * @param {number} shippingCost The cost of the selected shipping option
 * @param {string} shippingCode The code that corresponds with one of our shipping methods
 * @param {number} preAdjustedShippingCost the shipping cost prior to platform margin adjustments
 */
export function trackShippingMethodSelection(
    productId,
    productTaxonomy,
    shippingCost,
    shippingCode,
    preAdjustedShippingCost = 0,
) {
    logAmplitudeEvent(generateEventName('shipping method select'), {
        [AmplitudeEventProperty.PRODUCT_ID]: productId,
        [AmplitudeEventProperty.PRODUCT_TAXONOMY]: productTaxonomy,
        [AmplitudeEventProperty.SHIPPING_COST]: shippingCost,
        [AmplitudeEventProperty.SHIPPING_PARTNER_CODE]: shippingCode,
        // shipping engine cost is an estimate so long as we round shipping up to the next whole number ending in 9
        [AmplitudeEventProperty.SHIPPING_ENGINE_COST]: preAdjustedShippingCost,
    });
}

/**
 * Tracks when a cart or offer is submitted unsuccessfully
 *
 * @param {Object} data The checkout data that came back from the server with errors
 * @param {string=} paymentMethodValue
 * @param isExpressCheckout {boolean}
 *
 */
export function trackSubmitError(data, paymentMethodValue, isExpressCheckout) {
    logAmplitudeEvent(generateEventName('submit error'), {
        fields: getFieldNamesWithErrors(data).join(','),
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
    });
}

/**
 * Tracks that a payment request wallet is available.
 *
 * @param {string} paymentMethodValue
 * @param {boolean} isExpressCheckout
 */
export function trackPaymentRequestAvailable(paymentMethodValue, isExpressCheckout) {
    logAmplitudeEvent(generateEventName('payment request available'), {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
    });
}

/**
 * Tracks that a user initiated the payment request checkout flow.
 *
 * @param {boolean} isExpressCheckout
 * @param {string} paymentMethodValue
 */
export function trackPaymentRequestInitiated(paymentMethodValue, isExpressCheckout) {
    logAmplitudeEvent(generateEventName('payment request initiated'), {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
    });
}

/**
 * Tracks that a user authorized the payment from the system dialog.
 *
 * @param {string} paymentMethodValue
 * @param {boolean} isExpressCheckout
 */
export function trackPaymentRequestAuthorized(paymentMethodValue, isExpressCheckout) {
    logAmplitudeEvent(generateEventName('payment request authorized'), {
        [AmplitudeEventProperty.PAYMENT_METHOD]: getPaymentMethodName(paymentMethodValue, isExpressCheckout),
    });
}
